import { extendColors, inter, typography } from '@/config/theme/common';
import { createTheme } from '@mui/material/styles';
import { QlubKitTheme } from '@qlub-dev/qlub-kit';
import { palette } from '@qlub-dev/qlub-kit/dist/tokens/palette';

const theme = createTheme({
  palette: {
    ...QlubKitTheme.palette,
    error: QlubKitTheme.palette.error,
    success: QlubKitTheme.palette.success,
    primary: {
      ...QlubKitTheme.palette.primary,
      main: QlubKitTheme.palette.primary.main,
    },
  },
  qlub: {
    ...QlubKitTheme,
    palette: {
      success: {
        main: QlubKitTheme.palette.success.main,
      },
      circular: {
        main: QlubKitTheme.palette.primary.main,
      },
      onSurfaceColors: {
        disabled: QlubKitTheme.palette.primary.dark,
      },
      dim: {
        main: palette.dim.main,
      },
      danger: {
        main: palette.danger.main,
        transparent: palette.danger.transparent,
      },
      white: {
        main: palette.white.main,
      },
      iris: {
        main: palette.iris.main,
      },
      iris_light: {
        main: palette.iris.main,
      },
    },
  },
  typography,
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          title_sm: 'p',
        },
      },
      styleOverrides: {
        root: (props: any) => ({
          ...extendColors(props),
          fontFamily: inter.style.fontFamily,
        }),
      },
    },

    MuiDivider: {
      defaultProps: {
        sx: {
          borderColor: palette.dim,
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'unset',
          borderRadius: '100px',
          fontSize: '1rem',
        },
        contained: {
          background: palette.iris.main,
          color: palette.onColorColors.highEmphasis,
          border: `solid 1px transparent`,
          boxShadow: 'none',

          '&:hover': {
            background: palette.iris.main,
            boxShadow: 'none',
          },
          '&:focus': {
            background: palette.iris.main,
            boxShadow: 'none',
          },
          '&:active': {
            background: palette.iris.main,
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            border: '1px solid transparent',
            boxShadow: '0',
            background: palette.dim.main,
            color: palette.onSurfaceColors.disabled,
          },
        },
        text: {
          background: '#FFFFFF',
          border: `solid 1px transparent`,
          '&:focus': {
            border: `solid 1px ${palette.iris.main}`,
          },
          '&:hover': {
            background: '#FFFFFF',
          },
        },
        outlined: {
          backgroundColor: '#ffff',
          border: `1px solid ${palette.dim.main}`,
          '&:hover': {
            border: `1px solid ${palette.dim.main}`,
            background: palette.iris_light.hover,
          },
          '&:focus': {
            border: `1px solid ${palette.iris.main}`,
            background: palette.iris_light.focus,
          },
          '&:active': {
            border: `1px solid ${palette.dim.main}`,
            background: palette.iris_light.pressed,
          },
          '&.Mui-disabled': {
            border: '1px solid transparent',
            boxShadow: '0',
            background: palette.dim.main,
            color: palette.onSurfaceColors.disabled,
          },
        },
      },
      variants: [
        {
          props: {
            variant: 'containedLight',
          },
          style: {
            background: palette.iris_light.main,
            color: palette.iris.main,
            border: `solid 1px transparent`,
            '&:focus': {
              border: `solid 1px ${palette.iris.main}`,
              background: palette.iris_light.focus,
            },
            '&:hover': {
              background: palette.iris_light.hover,
            },
            '&:active': {
              background: palette.iris_light.pressed,
            },
            '&.Mui-disabled': {
              border: 'none',
              boxShadow: '0',
              background: palette.dim.main,
              color: palette.onSurfaceColors.disabled,
            },
          },
        },
        {
          props: {
            variant: 'containedDestructive',
          },
          style: {
            background: palette.destructive.main,
            color: palette.danger.main,
            border: `solid 1px transparent`,
            '&:focus': {
              border: `solid 1px ${palette.destructive.main}`,
              background: palette.destructive.focus,
            },
            '&:hover': {
              border: 'none',
              background: palette.destructive.hover,
            },
            '&:active': {
              border: 'none',
              background: palette.destructive.pressed,
            },
            '&.Mui-disabled': {
              border: 'none',
              boxShadow: '0',
              background: palette.dim.main,
              color: palette.onSurfaceColors.disabled,
            },
          },
        },
        {
          props: {
            variant: 'textElevated',
          },
          style: {
            boxShadow:
              '0px 2px 2px rgba(97, 100, 117, 0.06), 0px 3px 1px rgba(97, 100, 117, 0.04), 0px 1px 5px rgba(97, 100, 117, 0.12)',
            background: palette.white.main,
            color: palette.iris.main,
            '&:focus': {
              border: `solid 1px ${palette.iris.main}`,
              background: palette.iris_light.focus,
            },
            '&:hover': {
              background: palette.iris_light.hover,
            },
            '&:active': {
              background: palette.iris_light.pressed,
            },
            '&.Mui-disabled': {
              boxShadow: '0',
              background: palette.dim.main,
              color: palette.onSurfaceColors.disabled,
            },
          },
        },
        {
          props: {
            variant: 'grey',
          },
          style: {
            fontWeight: '400',
            fontSize: '0.875rem',
            background: palette.grey.main,
            color: palette.onSurfaceColors.highEmphasis,
            '&:focus': {
              background: palette.grey.main,
            },
            '&:hover': {
              background: palette.grey.main,
            },
            '&:active': {
              background: palette.grey.main,
            },
            '&.Mui-disabled': {
              boxShadow: '0',
              background: palette.dim.main,
              color: palette.onSurfaceColors.disabled,
            },
          },
        },
      ],
    },

    MuiChip: {
      styleOverrides: {
        sizeMedium: {
          padding: '0 3px',
          height: '38px',
          borderRadius: '20px',
        },
      },
      variants: [
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            borderColor: palette.dim.main,
            color: palette.onSurfaceColors.highEmphasis,
            backgroundColor: palette.dim.transparent,
          },
        },
        {
          props: { variant: 'filled', color: 'primary' },
          style: {
            backgroundColor: palette.iris.main,
            color: palette.white.main,
          },
        },
      ],
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          maxWidth: 'var(--app-max-width)',
          margin: '0 auto',
          borderRadius: '32px 32px 0 0',
          maxHeight: 'calc(100% - 16px)',
          '*': {
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',

            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
          '& :first-of-type>div>div>button': {
            padding: '8px',
            marginRight: '-8px',
          },
        },
      },
    },

    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '24px',
          paddingRight: '24px',
        },
      },
    },
  },
});

export { theme };
